import { z } from "zod";
import {
  centsToDecimal,
  decimalToCents,
  RATE_UNIT_LABELS,
} from "@/domain/billing";
import { isDateOnly, toDateOnly } from "@/domain/dates";
import {
  BILLING_CYCLES,
  BILLING_ITEM_TYPES,
  CURRENCIES,
  RATE_UNIT_PRESETS,
  WEEKDAYS,
  type ClientInput,
  type ClientProfile,
  type CurrencyCode,
  type DueRule,
} from "@/domain/types";
import { emptyAddress } from "./invoice-form";

export const clientFormSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, "Client name is required.")
      .max(150),
    company: z.string().trim().max(150),
    email: z
      .string()
      .trim()
      .max(254)
      .refine(
        (value) =>
          value === "" || z.email().safeParse(value).success,
        "Enter a valid email.",
      ),
    phone: z.string().trim().max(50),
    address: z.object({
      line1: z.string().trim().max(200),
      line2: z.string().trim().max(200),
      city: z.string().trim().max(100),
      state: z.string().trim().max(100),
      postalCode: z.string().trim().max(30),
      country: z.string().trim().max(100),
    }),
    currency: z.enum(CURRENCIES),
    billingCycle: z.enum(BILLING_CYCLES),
    billingType: z.enum(BILLING_ITEM_TYPES),
    rateUnitPreset: z.enum(RATE_UNIT_PRESETS),
    customRateUnit: z.string().trim().max(80),
    rate: z.number().min(0),
    dueKind: z.enum([
      "none",
      "upon_receipt",
      "specific",
      "net",
      "weekly",
      "every_n_weeks",
      "monthly",
      "custom",
    ]),
    dueSpecificDate: z.string(),
    dueNetDays: z.number().int().min(0).max(365),
    dueWeekday: z.enum(WEEKDAYS),
    dueIntervalWeeks: z.number().int().min(1).max(52),
    dueAnchorDate: z.string(),
    dueMonthlyDay: z.number().int().min(1).max(31),
    dueCustomLabel: z.string().trim().max(300),
    paymentTerms: z.string().trim().max(1000),
    paymentInstructions: z.string().trim().max(1000),
  })
  .superRefine((value, context) => {
    if (
      value.rateUnitPreset === "custom" &&
      !value.customRateUnit
    ) {
      context.addIssue({
        code: "custom",
        path: ["customRateUnit"],
        message: "Enter the agreed price unit.",
      });
    }

    if (
      value.dueKind === "specific" &&
      !isDateOnly(value.dueSpecificDate)
    ) {
      context.addIssue({
        code: "custom",
        path: ["dueSpecificDate"],
        message: "Enter a valid date.",
      });
    }

    if (
      value.dueKind === "every_n_weeks" &&
      !isDateOnly(value.dueAnchorDate)
    ) {
      context.addIssue({
        code: "custom",
        path: ["dueAnchorDate"],
        message: "Enter the first agreed due date.",
      });
    }

    if (
      value.dueKind === "custom" &&
      !value.dueCustomLabel
    ) {
      context.addIssue({
        code: "custom",
        path: ["dueCustomLabel"],
        message: "Describe the agreed payment terms.",
      });
    }
  });

export type ClientFormValues = z.infer<
  typeof clientFormSchema
>;

function dueFields(rule: DueRule) {
  return {
    dueKind: rule.kind,
    dueSpecificDate:
      rule.kind === "specific" ? rule.date : "",
    dueNetDays: rule.kind === "net" ? rule.days : 15,
    dueWeekday:
      rule.kind === "weekly"
        ? rule.weekday
        : ("friday" as const),
    dueIntervalWeeks:
      rule.kind === "every_n_weeks"
        ? rule.intervalWeeks
        : 2,
    dueAnchorDate:
      rule.kind === "every_n_weeks"
        ? rule.anchorDate
        : toDateOnly(),
    dueMonthlyDay:
      rule.kind === "monthly" ? rule.dayOfMonth : 15,
    dueCustomLabel:
      rule.kind === "custom" ? rule.label : "",
  };
}

function rateUnitFields(rateUnit: string): Pick<
  ClientFormValues,
  "rateUnitPreset" | "customRateUnit"
> {
  const normalized = rateUnit.trim().toLowerCase();

  const preset = RATE_UNIT_PRESETS.find(
    (candidate) =>
      candidate !== "custom" &&
      RATE_UNIT_LABELS[candidate] === normalized,
  );

  if (preset) {
    return {
      rateUnitPreset: preset,
      customRateUnit: "",
    };
  }

  if (!normalized) {
    return {
      rateUnitPreset: "hour",
      customRateUnit: "",
    };
  }

  return {
    rateUnitPreset: "custom",
    customRateUnit: rateUnit.trim(),
  };
}

function resolvedRateUnit(
  values: ClientFormValues,
): string {
  if (values.billingType === "hourly") {
    return "hour";
  }

  if (values.rateUnitPreset === "custom") {
    return values.customRateUnit.trim();
  }

  return RATE_UNIT_LABELS[values.rateUnitPreset];
}

export function clientDefaults(
  profile?: ClientProfile,
  defaultCurrency: CurrencyCode = "USD",
): ClientFormValues {
  const rule =
    profile?.defaults.dueRule ??
    ({ kind: "none" } as const);

  const billingType =
    profile?.defaults.billingType ??
    (profile?.defaults.hourlyRateCents
      ? "hourly"
      : "fixed");

  const rateCents =
    profile?.defaults.rateCents ??
    profile?.defaults.hourlyRateCents ??
    0;

  const rateUnit =
    profile?.defaults.rateUnit ||
    (billingType === "hourly" ? "hour" : "project");

  return {
    name: profile?.name ?? "",
    company: profile?.company ?? "",
    email: profile?.email ?? "",
    phone: profile?.phone ?? "",
    address: profile
      ? structuredClone(profile.address)
      : emptyAddress(),
    currency: profile?.defaults.currency ?? defaultCurrency,
    billingCycle:
      profile?.defaults.billingCycle ?? "weekly",
    billingType,
    ...rateUnitFields(rateUnit),
    rate: centsToDecimal(rateCents),
    ...dueFields(rule),
    paymentTerms:
      profile?.defaults.paymentTerms ?? "",
    paymentInstructions:
      profile?.defaults.paymentInstructions ?? "",
  };
}

function toRule(values: ClientFormValues): DueRule {
  if (values.dueKind === "none") {
    return { kind: "none" };
  }

  if (values.dueKind === "upon_receipt") {
    return { kind: "upon_receipt" };
  }

  if (values.dueKind === "specific") {
    return {
      kind: "specific",
      date: values.dueSpecificDate,
    };
  }

  if (values.dueKind === "net") {
    return { kind: "net", days: values.dueNetDays };
  }

  if (values.dueKind === "weekly") {
    return {
      kind: "weekly",
      weekday: values.dueWeekday,
    };
  }

  if (values.dueKind === "every_n_weeks") {
    return {
      kind: "every_n_weeks",
      intervalWeeks: values.dueIntervalWeeks,
      anchorDate: values.dueAnchorDate,
    };
  }

  if (values.dueKind === "monthly") {
    return {
      kind: "monthly",
      dayOfMonth: values.dueMonthlyDay,
    };
  }

  return {
    kind: "custom",
    label: values.dueCustomLabel,
  };
}

export function formToClientInput(
  values: ClientFormValues,
): ClientInput {
  const rateCents = decimalToCents(values.rate);

  return {
    name: values.name,
    company: values.company,
    email: values.email,
    phone: values.phone,
    address: structuredClone(values.address),
    defaults: {
      currency: values.currency,
      billingCycle: values.billingCycle,
      billingType: values.billingType,
      rateUnit: resolvedRateUnit(values),
      rateCents,
      hourlyRateCents:
        values.billingType === "hourly"
          ? rateCents
          : 0,
      dueRule: toRule(values),
      paymentTerms: values.paymentTerms,
      paymentInstructions: values.paymentInstructions,
    },
  };
}
